/**
 * Filter Controller — основной контроллер фильтров (UI/state)
 */

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

import { createFilters as categoriesCreateFilters, sortFilters as categoriesSortFilters, getSelectedCategories as categoriesGetSelected, computeAvailability as categoriesComputeAvailability } from './filter-controller.categories.js';
import { initializeUI as uiInitializeUI, updateFilterCounter as uiUpdateFilterCounter, clearAllFilters as uiClearAllFilters, applyAvailability as uiApplyAvailability, setupOutsideClickHandler as uiSetupOutsideClickHandler } from './filter-controller.ui.js';
import { setupEventListeners as eventsSetupEventListeners, handleFilterChange as eventsHandleFilterChange, handleDateInputChange as eventsHandleDateInputChange } from './filter-controller.events.js';
import { extractUrlParameters as urlExtractUrlParameters, updateUrlWithFilters as urlUpdateUrlWithFilters, flushUrlDebounce as urlFlushUrlDebounce } from './filter-controller.url.js';
import { refreshFiltersFromMetadata as metadataRefreshFiltersFromMetadata } from './filter-controller.metadata.js';


/** Filter Controller Class — manages all filter operations */
class FilterController {
    constructor() {
        this.filters = {};
        this.trees = {};
        this.activeFilters = {
            categories: {},
            dateFrom: '',
            dateTo: '',
            searchQuery: ''
        };
        
        this.filterElements = {
            container: '.filters-category-container',
            dateContainer: '.filter-date-container',
            clearButton: '.filters-clear',
            filterButton: '.filter-button',
            counter: '.fnd-filter-counter',
            dateInputs: '.filter-date',
            dateFrom: '.filter-date-from',
            dateTo: '.filter-date-to'
        };
        
        this.filterOrder = {
            'filters': ['L', 'M', 'F', 'S'], // UI display order
            'trees': ['S', 'L', 'F', 'M'],
            'L': ['EN', 'RU', 'DE', 'FR', 'ES', 'CN'],
            'M': ['Bu', 'PL', 'Le', 'Bk', 'Co', 'Di', 'Ot'],
            'F': ['Tx', 'Sc', 'Au', 'Vi'],
            'S': ['Hauri', 'FP', 'Other']
        };
        
        this.filterCallbacks = [];
        this.isInitialized = false;
        this.isInitializing = false; // Flag to prevent URL updates during initialization
        this.translations = {};
        this.language = 'EN';
        
        // Initialization logging only in debug mode
    }
    
    /**
     * Initialize filter controller
     * @param {Object} options - Configuration options
     * @returns {boolean} Success status
     */
    initialize(options = {}) {
        try {
            // Get translations from TranslationRegistry in old format for compatibility
            let translationsObj = {};
            if (window.TranslationRegistry && typeof window.TranslationRegistry.getAllTranslations === 'function') {
                const allTranslations = window.TranslationRegistry.getAllTranslations();
                // Convert to old format: { key: { EN: '...', RU: '...' } }
                translationsObj = allTranslations || {};
            }
            
            const config = {
                categories: {}, // Empty - filters loaded from filter_metadata in IndexedDB
                translations: translationsObj,
                language: window.LANG || 'EN',
                startCategories: {},
                ...options
            };
            
            // Save translations and language for later use
            this.translations = config.translations;
            this.language = config.language;
            
            // Set initializing flag to prevent URL updates during init
            this.isInitializing = true;
            
            // Extract URL parameters for initial filters
            this.extractUrlParameters(config);
            
            // Note: Filters are loaded from filter_metadata (IndexedDB) via refreshFiltersFromMetadata()
            // Called from load-data.js after IndexedDB is ready
            // No need to call createFilters() here - it expects CATEG format which is deprecated
            
            // Don't initialize UI here - it will be initialized when refreshFiltersFromMetadata is called
            // This prevents creating empty filter UI elements
            // Only setup event listeners and mark as initialized
            
            // Setup event listeners
            this.setupEventListeners();
            
            this.isInitialized = true;
            this.isInitializing = false; // Clear flag after initialization
            
            // Initialization logging only in debug mode
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FilterController] Initialization failed:', error);
            }
            return false;
        }
    }
    
    // Create filters from categories (delegated)
    createFilters(categories) { categoriesCreateFilters(this, categories); }
    
        // Sort filters according to predefined order (delegated)
        sortFilters(language) { categoriesSortFilters(this, language); }
    
        // Initialize filter UI components (delegated)
        initializeUI(config) { uiInitializeUI(this, config); }
    
    // Setup event listeners for filter controls (delegated)
    setupEventListeners() { eventsSetupEventListeners(this); }
    
    // Handle filter change event (delegated)
    handleFilterChange(key, selection) { eventsHandleFilterChange(this, key, selection); }
    
    // Handle date input change (delegated)
    handleDateInputChange(event) { eventsHandleDateInputChange(this, event); }
    
    clearAllFilters() { uiClearAllFilters(this); }
    
    // Update filter counter display (delegated)
    updateFilterCounter() { uiUpdateFilterCounter(this); }

    // Recompute and apply availability across groups
    refreshAvailability() {
        try {
            const availability = categoriesComputeAvailability(this);
            uiApplyAvailability(this, availability);
        } catch (e) {
            if (debugMode === '1') { console.error('[FilterController] refreshAvailability error', e); }
        }
    }
    
    /** Get selected categories (delegated) */
    getSelectedCategories() { return categoriesGetSelected(this); }
    
    // Extract URL parameters for initial filter state (delegated)
    extractUrlParameters(config) { urlExtractUrlParameters(this, config); }
    
    // Update URL with current filter state (delegated)
    updateUrlWithFilters(immediate = false) { 
        urlUpdateUrlWithFilters(this, immediate); 
    }
    
    // Flush URL debounce - immediately execute pending URL update (delegated)
    flushUrlDebounce() {
        urlFlushUrlDebounce(this);
    }
    
    /**
     * Add filter change callback
     * @param {Function} callback - Callback function
     */
    addFilterCallback(callback) {
        if (typeof callback === 'function') {
            this.filterCallbacks.push(callback);
        }
    }
    
    /**
     * Remove filter change callback
     * @param {Function} callback - Callback to remove
     */
    removeFilterCallback(callback) {
        const index = this.filterCallbacks.indexOf(callback);
        if (index > -1) {
            this.filterCallbacks.splice(index, 1);
        }
    }
    
    // Trigger all filter callbacks
    triggerFilterCallbacks() {
        const filterData = this.getActiveFilters();
        
        for (const callback of this.filterCallbacks) {
            try {
                callback(filterData);
            } catch (error) {
                if (debugMode === '1') {
                console.error('[FilterController] Error in filter callback:', error);
            }
            }
        }
    }
    
    /**
     * Refresh filters from metadata loaded from IndexedDB (delegated)
     * Also applies URL category parameters if metadata was not available during initial extractUrlParameters
     * @param {Object} filterMeta - Filter metadata with uniqueValues
     */
    refreshFiltersFromMetadata(filterMeta) { metadataRefreshFiltersFromMetadata(this, filterMeta); }
    
    /**
     * Get current active filters
     * @returns {Object} Active filters object
     */
    getActiveFilters() {
        return {
            categories: { ...this.activeFilters.categories },
            dateFrom: this.activeFilters.dateFrom,
            dateTo: this.activeFilters.dateTo,
            searchQuery: this.activeFilters.searchQuery,
            hasActiveFilters: this.hasActiveFilters()
        };
    }
    
    /**
     * Check if any filters are active
     * @returns {boolean} True if filters are active
     */
    hasActiveFilters() {
        const hasCategories = Object.keys(this.activeFilters.categories).length > 0;
        const hasDates = this.activeFilters.dateFrom || this.activeFilters.dateTo;
        const hasSearch = this.activeFilters.searchQuery.length > 0;
        
        return hasCategories || hasDates || hasSearch;
    }
    
    /**
     * Set date filter values
     * @param {string} dateFrom - From date
     * @param {string} dateTo - To date
     */
    setDateFilters(dateFrom, dateTo) {
        if (dateFrom) {
            this.activeFilters.dateFrom = dateFrom;
            $(this.filterElements.dateFrom).val(dateFrom);
        }
        
        if (dateTo) {
            this.activeFilters.dateTo = dateTo;
            $(this.filterElements.dateTo).val(dateTo);
        }
        
        this.updateFilterCounter();
        this.triggerFilterCallbacks();
    }
    
    /**
     * Get filter configuration
     * @returns {Object} Filter configuration
     */
    getConfiguration() {
        return {
            filters: this.filters,
            trees: this.trees,
            activeFilters: this.activeFilters,
            filterOrder: this.filterOrder,
            isInitialized: this.isInitialized
        };
    }
    
    /**
     * Destroy filter controller
     */
    destroy() {
        try {
            // Remove event listeners
            $(this.filterElements.clearButton).off("click");
            $(this.filterElements.filterButton).off("click");
            $(this.filterElements.dateInputs).off("input");
            
            // Clear trees
            for (const tree of Object.values(this.trees)) {
                if (tree && tree.destroy) {
                    tree.destroy();
                }
            }
            
            // Reset state
            this.filters = {};
            this.trees = {};
            this.activeFilters = { categories: {}, dateFrom: '', dateTo: '', searchQuery: '' };
            this.filterCallbacks = [];
            this.isInitialized = false;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FilterController] Destroyed');
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FilterController] Error destroying filter controller:', error);
            }
        }
    }
}

// Create singleton instance
const filterController = new FilterController();

// Export for use in other modules
export { filterController };

// Global export for backward compatibility
window.filterController = filterController;

// Register with migration controller
//         'initialize',
//         'createFilters',
//         'sortFilters',
//         'initializeUI',
//         'handleFilterChange',
//         'clearAllFilters',
//         'updateFilterCounter',
//         'getActiveFilters',
//         'setDateFilters',
//         'addFilterCallback',
//         'destroy'
    
//     // Mark as migrated from main.js
// }

// Module registration logging only in debug mode 